/**
 * ตั้งค่า LINE — Owner กรอกค่า LIFF ID + Messaging API channel เอง แทนการ
 * hardcode ตอน build. LIFF ID ใช้ทันทีที่พนักงานเปิดแอปครั้งถัดไป (ไม่ต้อง deploy
 * ใหม่) — ดู lib/liff.ts (initLiff ดึงจาก /public/line-config ก่อน liff.init).
 * Channel secret / access token เป็น write-only (เก็บฝั่ง server เท่านั้น) —
 * เว้นว่าง = ไม่เปลี่ยนค่าที่ตั้งไว้แล้ว.
 */
import { useEffect, useState } from 'react';
import { Save, MessageCircle } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { LoadError } from '../../components/layout/LoadError';
import { useLineSettings, useUpdateLineSettings } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import { API_BASE } from '../../lib/api-client';

export function LineSettings() {
  const { data, isLoading, error, refetch } = useLineSettings();
  const update = useUpdateLineSettings();
  const toast = useToast();
  const confirm = useConfirm();

  const [liffId, setLiffId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [channelSecret, setChannelSecret] = useState('');
  const [channelAccessToken, setChannelAccessToken] = useState('');

  useEffect(() => {
    if (!data) return;
    setLiffId(data.liff_id);
    setChannelId(data.channel_id);
  }, [data]);

  if (isLoading) {
    return (
      <AdminLayout title="ตั้งค่า LINE">
        <Loading />
      </AdminLayout>
    );
  }
  if (error || !data) {
    return (
      <AdminLayout title="ตั้งค่า LINE">
        <LoadError error={error} onRetry={() => refetch()} />
      </AdminLayout>
    );
  }

  async function handleSave() {
    try {
      await update.mutateAsync({
        liff_id: liffId.trim(),
        channel_id: channelId.trim(),
        ...(channelSecret.trim() ? { channel_secret: channelSecret.trim() } : {}),
        ...(channelAccessToken.trim() ? { channel_access_token: channelAccessToken.trim() } : {}),
      });
      setChannelSecret('');
      setChannelAccessToken('');
      toast.success('บันทึกการตั้งค่า LINE แล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  async function handleClearSecrets() {
    if (!(await confirm({
      title: 'ล้าง Channel Secret และ Access Token',
      message: 'ระบบจะกลับไปเป็น "ยังไม่ได้ตั้งค่า" — แจ้งเตือนเข้าไลน์จะหยุดส่งจนกว่าจะกรอกใหม่ (การเข้าใช้งานของพนักงานไม่กระทบ เพราะใช้ LIFF ID กับ Channel ID)',
      confirmText: 'ล้างค่า',
    }))) return;
    try {
      await update.mutateAsync({ clear: ['channel_secret', 'channel_access_token'] });
      setChannelSecret('');
      setChannelAccessToken('');
      toast.success('ล้างค่าแล้ว — กรอกค่าจริงจากคอนโซล LINE ได้เลย');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ล้างค่าไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="ตั้งค่า LINE">
      <div className="space-y-4 max-w-lg">
        <div className="card p-4 flex items-start gap-3">
          <MessageCircle className="w-5 h-5 text-brand-600 mt-0.5 shrink-0" />
          <div className="text-sm text-gray-600 space-y-1">
            <p>สร้าง Messaging API channel + LIFF app ได้ที่ developers.line.biz — ตั้ง endpoint URL ของ LIFF app เป็นหน้าแอปนี้</p>
            <p>Webhook URL: <code className="bg-gray-100 px-1 text-xs break-all">{API_BASE}/webhook/line</code></p>
          </div>
        </div>

        <div className="card p-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium">LIFF ID</span>
            <input
              value={liffId}
              onChange={(e) => setLiffId(e.target.value)}
              className="input mt-1 w-full font-mono text-sm"
              placeholder="เช่น 1234567890-abcdefgh"
              autoComplete="off"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium">Channel ID</span>
            <input
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              className="input mt-1 w-full font-mono text-sm"
              placeholder="Messaging API channel ID"
              autoComplete="off"
            />
          </label>
        </div>

        <div className="card p-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium">Channel Secret</span>
            <input
              type="password"
              value={channelSecret}
              onChange={(e) => setChannelSecret(e.target.value)}
              placeholder={data.channel_secret_set ? 'ตั้งค่าแล้ว (เว้นว่างถ้าไม่เปลี่ยน)' : 'วาง Channel Secret ที่นี่'}
              className="input mt-1 w-full"
              autoComplete="off"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium">Channel Access Token</span>
            <input
              type="password"
              value={channelAccessToken}
              onChange={(e) => setChannelAccessToken(e.target.value)}
              placeholder={data.channel_access_token_set ? 'ตั้งค่าแล้ว (เว้นว่างถ้าไม่เปลี่ยน)' : 'วาง Channel Access Token ที่นี่'}
              className="input mt-1 w-full"
              autoComplete="off"
            />
          </label>
          <p className="text-xs text-gray-500">ค่าที่ตั้งไว้แล้วจะไม่แสดงซ้ำ — เว้นว่างช่องไหนคือไม่เปลี่ยนช่องนั้น</p>

          {/* เว้นว่าง = ไม่เปลี่ยน จึงลบค่าที่ตั้งผิดไม่ได้เลยถ้าไม่มีปุ่มนี้ —
              ค่าผิดแย่กว่ายังไม่ตั้ง เพราะระบบคิดว่าตั้งแล้วแล้วส่งไลน์ไม่ออกแบบเงียบ ๆ */}
          {(data.channel_secret_set || data.channel_access_token_set) && (
            <button
              type="button"
              onClick={handleClearSecrets}
              disabled={update.isPending}
              className="text-xs text-rose-600 hover:underline"
            >
              ล้าง Channel Secret และ Access Token (กรณีกรอกผิด)
            </button>
          )}
        </div>

        <button onClick={handleSave} disabled={update.isPending} className="btn-cta flex items-center gap-2">
          <Save className="w-4 h-4" /> {update.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
        </button>
      </div>
    </AdminLayout>
  );
}
